import { useCallback, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { OwnerSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { LoadingRow } from '../../components/LoadingRow.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { useLoadState } from '../../hooks/useLoadState.ts';
import { OwnerFormDialog } from './OwnerFormDialog.tsx';
import { OwnerShopsDialog } from './OwnerShopsDialog.tsx';
import { ownerDisplayName } from './ownersHelpers.ts';

export function OwnersPage(): React.JSX.Element {
  const { api, hasPermission } = useAuth();
  const canWrite = hasPermission('owners:write');

  const fetchOwners = useCallback(async () => (await api.owners.list()).owners, [api]);
  const { data: owners, state, reload } = useLoadState(fetchOwners, [] as OwnerSummary[]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<OwnerSummary | null>(null);
  const [shopsTarget, setShopsTarget] = useState<OwnerSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OwnerSummary | null>(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Owners</Typography>
        {canWrite && (
          <Button
            onClick={() => {
              setEditTarget(null);
              setDialogOpen(true);
            }}
          >
            Add owner
          </Button>
        )}
      </Box>

      {state.kind === 'loading' && <LoadingRow label="Loading owners..." />}

      {state.kind === 'error' && <LoadErrorAlert message={state.message} />}

      {state.kind === 'ready' && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Shops</TableCell>
                  {canWrite && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {owners.length === 0 && <EmptyRow colSpan={canWrite ? 5 : 4} message="No owners yet." />}
                {owners.map((owner) => (
                  <TableRow key={owner.id}>
                    <TableCell>{ownerDisplayName(owner)}</TableCell>
                    <TableCell>{owner.phone ?? '-'}</TableCell>
                    <TableCell>{owner.email ?? '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {owner.shops.map((s) => (
                          <Chip key={s.id} label={s.shop_code} size="small" />
                        ))}
                        {owner.shops.length === 0 && (
                          <Typography variant="body2" sx={{ opacity: 0.6 }}>
                            None
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {canWrite && (
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <QuietButton size="small" onClick={() => setShopsTarget(owner)}>
                            Manage shops
                          </QuietButton>
                          <QuietButton
                            size="small"
                            onClick={() => {
                              setEditTarget(owner);
                              setDialogOpen(true);
                            }}
                          >
                            Edit
                          </QuietButton>
                          <Button size="small" variant="outlined" color="error" onClick={() => setDeleteTarget(owner)}>
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {canWrite && (
        <>
          <OwnerFormDialog
            open={dialogOpen}
            owner={editTarget}
            onClose={() => setDialogOpen(false)}
            onSaved={() => void reload()}
          />

          <OwnerShopsDialog
            open={shopsTarget !== null}
            owner={shopsTarget}
            onClose={() => setShopsTarget(null)}
            onSaved={() => void reload()}
          />

          <ConfirmDialog
            open={deleteTarget !== null}
            title="Delete owner"
            body={
              deleteTarget
                ? `Delete ${ownerDisplayName(deleteTarget)}? This also removes any shop links for this owner.`
                : ''
            }
            confirmLabel="Delete"
            confirmColor="error"
            onConfirm={async () => {
              if (deleteTarget) await api.owners.remove(deleteTarget.id);
            }}
            onClose={() => setDeleteTarget(null)}
            onSuccess={() => void reload()}
          />
        </>
      )}
    </Box>
  );
}

export default OwnersPage;
